import React from 'react';
import {useSlateStatic, useSelected, useFocused} from 'slate-react';

import {useConfigStore} from '../../../store/reportStore/configStore';
import useDocumentStore from '../../../store/reportStore/documentStore';
import AnchorOptionsMenu from '../AnchorOptionsMenu';
import {useEditorUI} from '../editor-core/contexts/EditorUIContext';

import {ActionButtons} from './ActionButtons';
import {getWrapStyle} from './getWrapStyle';
import {ImageRenderer} from './ImageRenderer';
import {PageNumber} from './PageNumber';
import {PageRenderer} from './PageRenderer';
import {ResizableElement, ResizeContext} from './ResizableElement';
import {SidebarHtmlContent} from './SidebarHtmlContent';
import TableRenderer from './TableRenderer';

export const ElementRenderer = ({
  attributes,
  children,
  element,
  headerFooterEditMode: propHFMode = false,
  watermark: propWatermark,
  pageMargins: propMargins,
  pageOrientation: propPageOrientation,
  pageIndex
}: any): React.JSX.Element => {
  const editor = useSlateStatic();
  const headerFooterEditMode =
    (editor.isHeaderFooterEditMode ?? propHFMode) || false;
  const selected = useSelected();
  const focused = useFocused();
  const storeWatermark = useDocumentStore((s) => s.watermark);
  const storePageMargins = useDocumentStore((s) => s.pageMargins);
  const storePageOrientation = useDocumentStore((s) => s.pageOrientation);

  const {setCroppingImage} = useEditorUI();

  const watermark =
    propWatermark !== undefined ? propWatermark : storeWatermark;
  const pageMargins =
    propMargins !== undefined ? propMargins : storePageMargins;
  const pageOrientation =
    propPageOrientation !== undefined
      ? propPageOrientation
      : storePageOrientation;

  const rawLineHeight =
    element.lineHeight !== undefined ? element.lineHeight : 1.5;
  const firstLineIndentLevel = Math.max(
    0,
    Number(element.firstLineIndent || 0)
  );
  const style: any = {
    textAlign: element.align || 'left',
    lineHeight: Math.max(1, rawLineHeight),
    ...(element.backgroundColor
      ? {backgroundColor: element.backgroundColor}
      : {})
  };
  if (rawLineHeight === 0) {
    style.marginBottom = '0px';
    style.marginTop = '0px';
  } else if (rawLineHeight === 0.5) {
    style.marginBottom = '2px';
    style.marginTop = '2px';
  }

  const normalizeLinkUrl = (rawUrl: string): string => {
    const trimmed = (rawUrl || '').trim();
    if (!trimmed) return '';
    if (/^(https?:\/\/|mailto:|tel:)/i.test(trimmed)) return trimmed;

    return `https://${trimmed}`;
  };

  const renderContent = (): React.JSX.Element => {
    switch (element.type) {
      case 'page':
        return (
          <PageRenderer
            element={element}
            attributes={attributes}
            watermark={watermark}
            pageMargins={pageMargins}
            pageOrientation={pageOrientation}
          >
            {children}
          </PageRenderer>
        );
      case 'heading-one':
        return (
          <h1 style={style} {...attributes}>
            {children}
          </h1>
        );
      case 'heading-two':
        return (
          <h2 style={style} {...attributes}>
            {children}
          </h2>
        );
      case 'heading-three':
        return (
          <h3 style={style} {...attributes}>
            {children}
          </h3>
        );
      case 'block-quote':
        return (
          <blockquote style={style} {...attributes}>
            {children}
          </blockquote>
        );
      case 'paragraph':
        return (
          <p
            style={{
              ...style,
              textIndent:
                firstLineIndentLevel > 0 ? `${firstLineIndentLevel * 2}em` : 0
            }}
            {...attributes}
          >
            {children}
          </p>
        );
      case 'link':
        const href = normalizeLinkUrl(element.url || '');

        return (
          <a
            href={href}
            style={{...style, color: 'blue', textDecoration: 'underline'}}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (!href) return;
              window.open(href, '_blank', 'noopener,noreferrer');
            }}
            {...attributes}
          >
            {children}
          </a>
        );
      case 'columns-container':
        const count = Math.max(
          1,
          Number(
            element.columns ||
              (Array.isArray(element.children) ? element.children.length : 0) ||
              2
          )
        );

        return (
          <div
            {...attributes}
            className="editor-columns-container"
            style={{
              ...style,
              display: 'grid',
              gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`,
              columnGap: `${Math.max(8, Number(element.gap || 24))}px`,
              alignItems: 'start',
              margin: '12px 0',
              clear: 'both',
              width: '100%'
            }}
          >
            {children}
          </div>
        );
      case 'column':
        return (
          <div {...attributes} className="editor-column" style={{minWidth: 0}}>
            {children}
          </div>
        );
      case 'image':
        return (
          <ImageRenderer
            attributes={attributes}
            element={element}
            selected={selected}
            focused={focused}
            editor={editor}
          >
            {children}
          </ImageRenderer>
        );
      case 'placeholder-image':
        const config = useConfigStore.getState();
        const info = config.placeholderTypes[element.placeholderType] || {
          color: '#ccc',
          text: 'Unknown'
        };
        const phStyle = getWrapStyle(element.float || 'none');

        return (
          <div
            {...attributes}
            contentEditable={false}
            style={{
              ...style,
              float: phStyle.float,
              margin: phStyle.margin,
              display: phStyle.display,
              clear: phStyle.clear,
              position: 'relative',
              maxWidth: phStyle.maxWidth,
              verticalAlign: phStyle.verticalAlign,
              shapeOutside: phStyle.shapeOutside,
              WebkitShapeOutside: phStyle.WebkitShapeOutside,
              shapeMargin: phStyle.shapeMargin
            }}
          >
            <ResizableElement element={element}>
              <div
                data-resizable
                className="render-element-wrapper"
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: info.color,
                  border: '2px dashed #999',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: info.shape === 'circle' ? '50%' : 4,
                  fontSize: 14,
                  fontWeight: '600',
                  color: '#333',
                  position: 'relative',
                  boxShadow: selected && focused ? '0 0 0 2px #1a73e8' : 'none'
                }}
              >
                <ActionButtons
                  editor={editor}
                  element={element}
                  setCroppingImage={setCroppingImage}
                />
                <AnchorOptionsMenu element={element} />
                {info.text}
              </div>
            </ResizableElement>
            {children}
          </div>
        );
      case 'shape':
        const shStyle = getWrapStyle(element.float || 'none');

        return (
          <div
            {...attributes}
            contentEditable={false}
            style={{
              ...style,
              float: shStyle.float,
              margin: shStyle.margin,
              display: shStyle.display,
              clear: shStyle.clear,
              position: 'relative',
              textAlign: element.align || 'left'
            }}
          >
            <ResizableElement element={element}>
              <ResizeContext.Consumer>
                {(resize) => {
                  const sW = (resize?.width ?? element.width) || 100;
                  const sH = (resize?.height ?? element.height) || 100;

                  return (
                    <div
                      data-resizable
                      className="render-element-wrapper"
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'inline-block',
                        position: 'relative',
                        boxShadow:
                          selected && focused ? '0 0 0 2px #1a73e8' : 'none'
                      }}
                    >
                      <ActionButtons
                        editor={editor}
                        element={element}
                        setCroppingImage={setCroppingImage}
                      />
                      <AnchorOptionsMenu element={element} />
                      <svg
                        width="100%"
                        height="100%"
                        viewBox={`0 0 ${sW} ${sH}`}
                        style={{overflow: 'visible'}}
                      >
                        {element.shapeType === 'rectangle' && (
                          <rect
                            x="2"
                            y="2"
                            width={sW - 4}
                            height={sH - 4}
                            fill="#e8f0fe"
                            stroke="#1a73e8"
                            strokeWidth="2"
                          />
                        )}
                        {element.shapeType === 'circle' && (
                          <circle
                            cx={sW / 2}
                            cy={sH / 2}
                            r={Math.min(sW, sH) / 2 - 4}
                            fill="#e8f0fe"
                            stroke="#1a73e8"
                            strokeWidth="2"
                          />
                        )}
                        {element.shapeType === 'triangle' && (
                          <polygon
                            points={`${sW / 2},2 ${sW - 2},${sH - 2} 2,${
                              sH - 2
                            }`}
                            fill="#e8f0fe"
                            stroke="#1a73e8"
                            strokeWidth="2"
                          />
                        )}
                        {element.shapeType === 'arrow' && (
                          <path
                            d={`M ${sW / 2} 2 L ${sW - 2} ${sH / 2} L ${
                              sW * 0.6
                            } ${sH / 2} L ${sW * 0.6} ${sH - 2} L ${sW * 0.4} ${
                              sH - 2
                            } L ${sW * 0.4} ${sH / 2} L 2 ${sH / 2} Z`}
                            fill="#e8f0fe"
                            stroke="#1a73e8"
                            strokeWidth="2"
                          />
                        )}
                        {element.shapeType === 'star' && (
                          <polygon
                            points={`${sW / 2},2 ${sW * 0.6},${sH * 0.4} ${
                              sW - 2
                            },${sH * 0.4} ${sW * 0.65},${sH * 0.6} ${
                              sW * 0.8
                            },${sH - 2} ${sW / 2},${sH * 0.7} ${sW * 0.2},${
                              sH - 2
                            } ${sW * 0.35},${sH * 0.6} 2,${sH * 0.4} ${
                              sW * 0.4
                            },${sH * 0.4}`}
                            fill="#e8f0fe"
                            stroke="#1a73e8"
                            strokeWidth="2"
                          />
                        )}
                      </svg>
                    </div>
                  );
                }}
              </ResizeContext.Consumer>
            </ResizableElement>
            {children}
          </div>
        );
      case 'bulleted-list':
        return (
          <ul
            style={{
              ...style,
              paddingLeft: '0em',
              margin: '0.25em 0',
              // Keep bullets inside the padding so they align consistently
              // with other content when lists are nested/indented.
              listStylePosition: 'inside'
            }}
            {...attributes}
          >
            {children}
          </ul>
        );
      case 'numbered-list':
        return (
          <ol
            style={{
              ...style,
              paddingLeft: '0em',
              margin: '0.25em 0',
              // Keep bullets inside the padding so they align consistently
              // with other content when lists are nested/indented.
              listStylePosition: 'inside'
            }}
            {...attributes}
          >
            {children}
          </ol>
        );
      case 'list-item':
        const itemIndent = Math.max(0, Number(element.indent || 0));

        return (
          <li
            style={{
              ...style,
              textAlign: element.align || 'inherit',
              marginLeft: itemIndent > 0 ? `${itemIndent * 2}em` : 0
            }}
            {...attributes}
          >
            {children}
          </li>
        );
      case 'table':
        return (
          <TableRenderer
            element={element}
            attributes={attributes}
            style={style}
          >
            {children}
          </TableRenderer>
        );
      case 'table-row':
        return <tr {...attributes}>{children}</tr>;
      case 'table-cell':
        return (
          <td
            className="editor-table-cell"
            style={{
              border: '1px solid #ccc',
              padding: '8px',
              backgroundColor: element.backgroundColor || 'inherit',
              display: element.merged ? 'none' : undefined,
              textAlign: element.align || 'left'
            }}
            colSpan={element.colSpan}
            rowSpan={element.rowSpan}
            {...attributes}
          >
            {children}
          </td>
        );

      case 'page-number':
        return (
          <PageNumber
            attributes={attributes}
            element={element}
            pageIndex={pageIndex}
          >
            {children}
          </PageNumber>
        );
      case 'framework-question':
        const fCol =
          element.framework === 'brsr'
            ? '#4285f4'
            : element.framework === 'gri'
              ? '#34a853'
              : '#fbbc04';

        return (
          <div {...attributes} style={style}>
            <div
              className="rnd-block"
              contentEditable={false}
              style={{
                width: element.width || 400,
                height: element.height || 150,
                position: 'relative',
                margin: '16px 0',
                border: '1px solid #ddd',
                padding: 0,
                boxShadow: selected && focused ? '0 0 0 2px #1a73e8' : 'none'
              }}
            >
              <div
                className="block-content"
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <div
                  className="framework-badge"
                  style={{
                    backgroundColor: fCol,
                    color: 'white',
                    padding: '4px 8px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    borderTopLeftRadius: '3px',
                    borderBottomRightRadius: '3px',
                    display: 'inline-block',
                    alignSelf: 'flex-start'
                  }}
                >
                  {element.framework
                    ? element.framework.toUpperCase()
                    : 'Question'}
                </div>
                <div className="question-content" style={{padding: '12px'}}>
                  {element.key && (
                    <div
                      className="question-key"
                      style={{
                        fontSize: '11px',
                        color: '#666',
                        marginBottom: '4px'
                      }}
                    >
                      {element.key}
                    </div>
                  )}
                  <div
                    className="question-text"
                    style={{fontSize: '14px', fontWeight: '500'}}
                  >
                    {element.question || 'Question Text'}
                  </div>
                </div>
              </div>
            </div>
            {children}
          </div>
        );
      case 'html-block':
        return (
          <div {...attributes} style={style}>
            <div contentEditable={false}>
              <SidebarHtmlContent html={element.htmlContent || ''} />
            </div>
            {children}
          </div>
        );
      case 'spacer': {
        const spacerHeights: Record<string, number> = {
          sm: 8,
          md: 16,
          lg: 32,
          xl: 64
        };
        const spacerH = spacerHeights[element.size] ?? 16;

        return (
          <div
            {...attributes}
            className="document-spacer"
            style={{
              height: `${spacerH}px`,
              lineHeight: 0,
              fontSize: 0,
              margin: 0,
              padding: 0
            }}
          >
            {children}
          </div>
        );
      }
      case 'page-break':
        return (
          <div
            {...attributes}
            contentEditable={false}
            className="page-break-indicator"
            style={{
              height: '0px',
              margin: '0px',
              padding: '0px',
              border: 'none',
              userSelect: 'none',
              pointerEvents: 'none',
              overflow: 'hidden'
            }}
          >
            {children}
          </div>
        );
      case 'header-wrapper':
        const mTPx = (pageMargins?.top ?? 0.5) * 96;
        const mLPx = (pageMargins?.left ?? 0.5) * 96;
        const mRPx = (pageMargins?.right ?? 0.5) * 96;
        const hStyle: any = {
          position: 'relative',
          marginTop: `-${mTPx}px`,
          marginLeft: `-${mLPx}px`,
          width: `calc(100% + ${mLPx + mRPx}px)`,
          minHeight: `${mTPx}px`,
          boxSizing: 'border-box',
          zIndex: 5,
          display: 'flex',
          flexDirection:
            element.headerType === 'three-columns' ? 'row' : 'column',
          justifyContent:
            element.headerType === 'three-columns'
              ? 'space-between'
              : 'flex-end',
          paddingLeft: `${mLPx}px`,
          paddingRight: `${mRPx}px`,
          paddingBottom: '10px',
          borderBottom: '1px dashed #d1d1d1',
          ...style
        };
        if (element.headerType === 'austin') {
          hStyle.border = '2px solid #333';
          hStyle.padding = '10px';
          hStyle.marginRight = '20px';
          hStyle.marginLeft = `calc(-${mLPx}px + 20px)`;
          hStyle.width = `calc(100% + ${mLPx + mRPx}px - 40px)`;
          hStyle.marginBottom = '10px';
        } else if (element.headerType === 'banded') {
          hStyle.backgroundColor = '#2c3e50';
          hStyle.color = 'white';
          hStyle.padding = '10px 40px';
          hStyle.justifyContent = 'center';
          hStyle.marginBottom = '10px';
        } else {
          hStyle.marginBottom = '10px';
        }

        return (
          <div
            {...attributes}
            style={hStyle}
            className={`header-wrapper-${element.headerType} ${
              headerFooterEditMode ? 'hf-editing' : 'hf-inactive'
            }`}
            data-header-footer="true"
            contentEditable={headerFooterEditMode}
          >
            {children}
          </div>
        );
      case 'footer-wrapper':
        const mBPx = (pageMargins?.bottom ?? 0.5) * 96;
        const mlPx = (pageMargins?.left ?? 0.5) * 96;
        const mrPx = (pageMargins?.right ?? 0.5) * 96;
        const fStyle: any = {
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          minHeight: `${mBPx}px`,
          height:
            element.footerType === 'three-columns' ? 'auto' : `${mBPx}px`,
          display: 'flex',
          flexDirection:
            element.footerType === 'three-columns' ? 'row' : 'column',
          justifyContent:
            element.footerType === 'three-columns'
              ? 'space-between'
              : 'flex-start',
          alignItems:
            element.footerType === 'three-columns' ? 'center' : 'stretch',
          paddingLeft: `${mlPx}px`,
          paddingRight: `${mrPx}px`,
          paddingTop: '10px',
          boxSizing: 'border-box',
          zIndex: 5,
          ...style,
          marginLeft: 0,
          borderTop: '1px dashed #d1d1d1'
        };
        if (element.footerType === 'austin') {
          fStyle.border = '2px solid #333';
          fStyle.padding = '10px';
          // fStyle.marginRight = '20px';
          fStyle.marginLeft = '20px';
          fStyle.width = 'calc(100% - 40px)';
          // fStyle.marginBottom = '10px';
        } else if (element.footerType === 'banded') {
          fStyle.backgroundColor = '#2c3e50';
          fStyle.color = 'white';
          fStyle.paddingTop = '14px';
          fStyle.justifyContent = 'flex-start';
        }

        return (
          <div
            {...attributes}
            style={fStyle}
            className={`footer-wrapper-${element.footerType} ${
              headerFooterEditMode ? 'hf-editing' : 'hf-inactive'
            }`}
            data-header-footer="true"
            contentEditable={headerFooterEditMode}
          >
            {children}
          </div>
        );
      default:
        return (
          <p style={style} {...attributes}>
            {children}
          </p>
        );
    }
  };

  const content = renderContent();
  if (Array.isArray(element.comments) && element.comments.length > 0) {
    return (
      <div
        data-has-comments="true"
        style={{...(content.props.style || {}), position: 'relative'}}
      >
        {content}
      </div>
    );
  }

  return content;
};
